/**
 * CreateHotelStep2 — 5-field Hotel Administrator creation (v2.1 §24.3).
 * User revision #1: role name is "Hotel Administrator" (not Hotel Owner).
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserPlus, CheckCircle2, AlertTriangle, Copy } from "lucide-react";
import api from "@/lib/api";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "bn", label: "Bengali" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "ar", label: "Arabic" },
  { code: "fr", label: "French" },
  { code: "es", label: "Spanish" },
];

export default function CreateHotelStep2() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [f, setF] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_language: "en",
    password: "",
    send_invitation: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/platform/v1/hotels/${hotelId}`);
        setHotel(data);
        if (data.has_administrator) {
          setErr("This hotel already has an administrator.");
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load hotel");
      }
    })();
  }, [hotelId]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const payload = { ...f };
      if (!payload.phone) delete payload.phone;
      if (payload.send_invitation) delete payload.password;
      else delete payload.send_invitation;
      const { data } = await api.post(
        `/platform/v1/hotels/${hotelId}/administrator`,
        payload,
      );
      setDone(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(Array.isArray(detail) ? detail.map((x) => x.msg || JSON.stringify(x)).join(", ") : (detail || "Failed to create administrator"));
    } finally {
      setSubmitting(false);
    }
  };

  const copyPwd = () => {
    if (done?.temporary_password) {
      navigator.clipboard.writeText(done.temporary_password);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl" data-testid="create-hotel-step2-success">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-8 shadow-sm">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mb-4" />
          <h1 className="text-2xl font-semibold text-slate-900">
            Hotel is live.
          </h1>
          <p className="text-slate-700 mt-2">
            <strong>{hotel?.name}</strong> is now{" "}
            <span className="text-emerald-700 font-semibold">{hotel?.trial_requested ? "trial" : "active"}</span> and the
            Hotel Administrator has been created.
          </p>
          <dl className="mt-5 space-y-2 text-sm">
            <div className="flex gap-3">
              <dt className="text-slate-500 w-28">Name</dt>
              <dd className="text-slate-900 font-medium">{done.name}</dd>
            </div>
            <div className="flex gap-3">
              <dt className="text-slate-500 w-28">Login email</dt>
              <dd className="text-slate-900 font-medium mono">{done.email}</dd>
            </div>
            {done.temporary_password && (
              <div className="flex gap-3 items-center">
                <dt className="text-slate-500 w-28">Temp password</dt>
                <dd className="text-amber-700 font-medium mono">{done.temporary_password}</dd>
                <button onClick={copyPwd} className="p-1 rounded hover:bg-slate-100" title="Copy">
                  <Copy className="w-3.5 h-3.5 text-slate-500" />
                </button>
              </div>
            )}
          </dl>
          <div className="mt-6 flex gap-3">
            <button
              data-testid="create-hotel-step2-continue"
              onClick={() => navigate(`/platform/hotels/${hotelId}/services-setup`)}
              className="px-4 py-2.5 rounded-lg bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 shadow-sm"
            >
              Continue → Configure Services
            </button>
            <button
              data-testid="create-hotel-step2-done"
              onClick={() => navigate("/platform/hotels")}
              className="px-4 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium"
            >
              Skip — back to Hotels
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl" data-testid="create-hotel-step2">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] text-amber-600 font-semibold">
          <UserPlus className="w-3.5 h-3.5" />
          Step 2 of 2 · Hotel Administrator
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 mt-2">
          {hotel?.name ? `Create the first admin for ${hotel.name}` : "Create Hotel Administrator"}
        </h1>
        <p className="text-slate-600 mt-2 text-sm">
          The Hotel Administrator has full access to this hotel. Ownership can be
          transferred later.
        </p>
      </header>

      {err && (
        <div
          className="rounded-lg bg-rose-50 border border-rose-200 p-4 text-rose-800 text-sm flex items-start gap-2 mb-6"
          data-testid="create-hotel-step2-error"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}

      <form onSubmit={submit} className="space-y-5">
        <Field label="Full Name" required>
          <input
            data-testid="create-hotel-step2-name"
            required
            value={f.name}
            onChange={(e) => set("name", e.target.value)}
            className="pl-input"
          />
        </Field>

        <Field label="Email" required>
          <input
            data-testid="create-hotel-step2-email"
            type="email"
            required
            value={f.email}
            onChange={(e) => set("email", e.target.value)}
            className="pl-input"
          />
        </Field>

        <Field label="Phone">
          <input
            data-testid="create-hotel-step2-phone"
            value={f.phone}
            onChange={(e) => set("phone", e.target.value)}
            className="pl-input"
          />
        </Field>

        <Field label="Preferred Language" required>
          <select
            data-testid="create-hotel-step2-language"
            required
            value={f.preferred_language}
            onChange={(e) => set("preferred_language", e.target.value)}
            className="pl-input"
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
        </Field>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              data-testid="create-hotel-step2-mode-password"
              type="radio"
              name="mode"
              checked={!f.send_invitation}
              onChange={() => set("send_invitation", false)}
              className="w-4 h-4 text-amber-500"
            />
            <span className="text-sm text-slate-800 font-medium">Set password now</span>
          </label>
          {!f.send_invitation && (
            <input
              data-testid="create-hotel-step2-password"
              type="password"
              placeholder="Password (min 8 characters)"
              value={f.password}
              onChange={(e) => set("password", e.target.value)}
              required={!f.send_invitation}
              minLength={8}
              className="pl-input ml-7"
              style={{ width: "calc(100% - 1.75rem)" }}
            />
          )}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              data-testid="create-hotel-step2-mode-invite"
              type="radio"
              name="mode"
              checked={f.send_invitation}
              onChange={() => set("send_invitation", true)}
              className="w-4 h-4 text-amber-500"
            />
            <span className="text-sm text-slate-800 font-medium">Send invitation (temp password shown after creation)</span>
          </label>
        </div>

        <div className="pt-4 flex items-center gap-3">
          <button
            data-testid="create-hotel-step2-submit"
            type="submit"
            disabled={submitting || !f.name || !f.email || (!f.send_invitation && !f.password)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {submitting ? "Creating…" : "Create Hotel Administrator"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/platform/hotels")}
            className="px-4 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium"
          >
            Cancel
          </button>
        </div>
      </form>

      <style>{`
        .pl-input {
          display: block; width: 100%;
          border-radius: 0.5rem;
          border: 1px solid rgb(203 213 225);
          background: rgb(255 255 255);
          padding: 0.625rem 0.875rem;
          color: rgb(15 23 42); font-size: 0.875rem;
        }
        .pl-input:focus {
          outline: 2px solid rgb(245 158 11);
          border-color: transparent;
        }
        .pl-input::placeholder { color: rgb(148 163 184); }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-2 text-[13px] font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-amber-600">*</span>}
      </span>
      {children}
    </label>
  );
}
